"use client";

import { useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Container } from "./Primitives";

type NavItem = { label: string; href: string; highlight?: boolean };

/**
 * Резолвер href для navigation-пунктов с anchor-ссылками.
 *
 * Логика:
 *  - href начинается с "/" или "http" — возвращается как есть (PMEF и т.п.).
 *  - href === "#events" — особый случай: на главной это якорь, на других
 *    страницах ведёт на полный список /events.
 *  - Прочие #-ссылки на не-главной получают префикс "/" → "/#about" и т.д.,
 *    чтобы браузер сначала перешёл на главную, потом скроллнул к якорю.
 */
function resolveHref(href: string, isHome: boolean): string {
  if (!href.startsWith("#")) return href;
  if (href === "#events" && !isHome) return "/events";
  return isHome ? href : `/${href}`;
}

const NAV_BASE: NavItem[] = [
  { label: "Услуги", href: "#services" },
  { label: "Автомобили", href: "#cars" },
  { label: "Мероприятия", href: "#events" },
  { label: "Достижения", href: "#achievements" },
  { label: "О нас", href: "#about" },
  { label: "Контакты", href: "#contacts" },
];

/**
 * Header — sticky-less top bar on white card.
 *
 * Логотип и телефон берутся только из админ-настроек. Если поле пустое —
 * на лендинге будет пусто (помогает убедиться что данные реально идут
 * из БД, а не подменяются захардкоженными дефолтами).
 */
export default function Header({
  logo,
  phone,
  currentEvent,
}: {
  logo?: string;
  phone?: string;
  /** Промо-плашка справа от nav. Если undefined или disabled — не рисуем. */
  currentEvent?: {
    enabled?: boolean;
    show_in_header?: boolean;
    header_label?: string;
    slug?: string;
  };
} = {}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === "/";
  const phoneTel = phone ? phone.replace(/[^\d+]/g, "") : "";

  // Динамическая навигация: к базовому списку добавляем промо-плашку текущего
  // мероприятия, если оно включено в admin-настройках.
  const NAV: NavItem[] =
    currentEvent?.enabled && currentEvent.show_in_header && currentEvent.slug
      ? [
          ...NAV_BASE,
          {
            label: currentEvent.header_label || "Мероприятие",
            href: `/event/${currentEvent.slug}`,
            highlight: true,
          },
        ]
      : NAV_BASE;

  return (
    <header className="bg-white">
      <Container className="py-5 sm:py-7 lg:py-9">
        <div className="flex items-center justify-between gap-6">
          {/* Logo строго из настроек админки. Если не загружено — место
              остаётся пустым (визуальная проверка что данные реально
              приходят из БД). */}
          <a href="/" className="flex items-center gap-3 shrink-0">
            {logo && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={logo}
                alt="AUTOPILOT.COMPANY"
                className="h-9 w-auto sm:h-10 lg:h-[45px]"
              />
            )}
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10 text-[15px] text-text">
            {NAV.map((n) => {
              const href = resolveHref(n.href, isHome);
              return n.highlight ? (
                <a
                  key={n.href}
                  href={href}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-white transition hover:bg-primary-hover"
                >
                  {n.label}
                </a>
              ) : (
                <a
                  key={n.href}
                  href={href}
                  className="transition hover:text-primary"
                >
                  {n.label}
                </a>
              );
            })}
          </nav>

          {/* Desktop phone */}
          <a
            href={`tel:${phoneTel}`}
            className="hidden lg:flex items-center gap-2 text-[15px] text-text transition hover:text-primary"
          >
            <Image src="/img/tagjs/neirryzc.png" alt="" width={28} height={28} />
            <span className="whitespace-nowrap">{phone}</span>
          </a>

          {/* Mobile burger */}
          <button
            type="button"
            aria-label={menuOpen ? "Закрыть меню" : "Открыть меню"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden inline-flex size-11 items-center justify-center rounded-full bg-bg text-text transition hover:bg-black/10"
          >
            {/* Анимированная иконка: 3 линии в hamburger / крест в open. */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              {menuOpen ? (
                <>
                  <path d="M6 6l12 12" />
                  <path d="M18 6L6 18" />
                </>
              ) : (
                <>
                  <path d="M4 7h16" />
                  <path d="M4 12h16" />
                  <path d="M4 17h16" />
                </>
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu — карточкой на фоне bg-bg, нагляднее с padding'ами и
            разделителями. Phone CTA отдельным блоком внизу. */}
        {menuOpen && (
          <nav className="lg:hidden mt-5 overflow-hidden rounded-[20px] bg-bg p-3">
            <ul className="flex flex-col">
              {NAV.map((n, i) => {
                const href = resolveHref(n.href, isHome);
                return (
                  <li key={n.href}>
                    {n.highlight ? (
                      <a
                        href={href}
                        onClick={() => setMenuOpen(false)}
                        className="my-2 flex items-center justify-between rounded-[14px] bg-primary px-5 py-4 text-[16px] font-medium text-white transition hover:bg-primary-hover"
                      >
                        <span>{n.label}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </a>
                    ) : (
                      <a
                        href={href}
                        onClick={() => setMenuOpen(false)}
                        className={`flex items-center justify-between px-4 py-4 text-[16px] text-text transition hover:text-primary ${
                          i < NAV.filter((x) => !x.highlight).length - 1
                            ? "border-b border-black/[0.07]"
                            : ""
                        }`}
                      >
                        <span>{n.label}</span>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-text-muted"
                        >
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>

            <a
              href={`tel:${phoneTel}`}
              className="mt-3 flex items-center gap-3 rounded-[14px] bg-white px-5 py-4 text-[16px] font-medium text-text transition hover:text-primary"
            >
              <Image src="/img/tagjs/neirryzc.png" alt="" width={22} height={22} />
              {phone}
            </a>
          </nav>
        )}
      </Container>
    </header>
  );
}
